import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useBlocker } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Cookies from "universal-cookie";
import {
  ArrowRight,
  Check,
  CircleAlert,
  Eye,
  FileText,
  Save,
  Search,
  ChartNoAxesCombined,
  ImagePlus,
} from "lucide-react";
import { api, apiFetch } from "../../../commons/axiosReq";
import { siteUrl } from "../../../config/urls";
import ArticleRichEditor, { assetUrl } from "./ArticleRichEditor";
import {
  articlePayload,
  inspectArticle,
  makeSlug,
  plainText,
} from "./articleContent";
import "./Articles.css";

const emptyArticle = {
  articleTittle: "",
  articleTittleEn: "",
  metaTitle: "",
  metaTitleEn: "",
  metaDescription: "",
  metaDescriptionEn: "",
  secondTitle: "",
  secondTitleEn: "",
  slug: "",
  slugEn: "",
  newsBox: "",
  newsBoxEn: "",
  newsText: "",
  newsTextEn: "",
  mainPicture: "",
  mainPage: false,
  isLast: false,
  isProminent: false,
  isMostViewed: false,
};
const escapeHtml = (value = "") =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ],
  );

export default function ArticleWorkspace({ editing = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const slug =
    new URLSearchParams(location.search).get("slug") || location.state?.id;
  const [article, setArticle] = useState(emptyArticle);
  const [options, setOptions] = useState({ categories: [], tags: [] });
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  const [mediaPending, setMediaPending] = useState(false);
  const [tab, setTab] = useState("write");
  const [contentLanguage, setContentLanguage] = useState("fa");
  const [schedule, setSchedule] = useState("");
  const [publication, setPublication] = useState(editing ? "keep" : "now");
  const [dirty, setDirty] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [reload, setReload] = useState(0);
  const coverInput = useRef(null);
  const allowNavigation = useRef(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      !allowNavigation.current &&
      currentLocation.pathname + currentLocation.search !==
        nextLocation.pathname + nextLocation.search,
  );
  const isAdmin = new Cookies().get("role") === "Admin";
  const localizedArticle = useMemo(() => contentLanguage === "en" ? {
    ...article,
    articleTittle: article.articleTittleEn || "",
    secondTitle: article.secondTitleEn || "",
    slug: article.slugEn || "",
    newsBox: article.newsBoxEn || "",
    newsText: article.newsTextEn || "",
  } : article, [article, contentLanguage]);
  const analysis = useMemo(() => inspectArticle(localizedArticle), [localizedArticle]);
  const englishStarted = [article.articleTittleEn, article.secondTitleEn, article.slugEn, article.newsBoxEn, article.newsTextEn].some((value) => plainText(value || ""));
  const validation = {
    ...Object.fromEntries(["metaTitle", "metaTitleEn", "metaDescription", "metaDescriptionEn"].map(name => [name, (article[name] || "").length > (name.includes("Description") ? 500 : 200) && "عنوان متا حداکثر ۲۰۰ و توضیحات متا حداکثر ۵۰۰ نویسه است."])),
    articleTittle: !article.articleTittle?.trim() && "عنوان مقاله را بنویسید.",
    slug:
      (!article.slug?.trim() || /[\s/?#]/.test(article.slug)) &&
      "نامک معتبر و بدون فاصله، /، ? یا # وارد کنید.",
    newsText: !plainText(article.newsText) && "متن مقاله را بنویسید.",
    articleTittleEn: englishStarted && !article.articleTittleEn?.trim() && "English article title is required.",
    slugEn: englishStarted && (!article.slugEn?.trim() || /[\s/?#]/.test(article.slugEn)) && "Enter a valid English slug without spaces, /, ? or #.",
    newsTextEn: englishStarted && !plainText(article.newsTextEn) && "English article content is required.",
    categories: !categories.length && "حداقل یک دسته‌بندی انتخاب کنید.",
    tags: !tags.length && "حداقل یک برچسب انتخاب کنید.",
    mainPicture: !article.mainPicture && "تصویر شاخص انتخاب کنید.",
    schedule:
      publication === "schedule" &&
      (!schedule || new Date(`${schedule}+03:30`).getTime() <= Date.now()) &&
      "زمان انتشار آینده را به وقت تهران انتخاب کنید.",
  };
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setLoadError("");
      if (editing && !slug) {
        setLoadError(
          "مقاله مشخص نشده است. از گزارش مقالات، ویرایش مقاله موردنظر را باز کنید.",
        );
        setLoading(false);
        return;
      }
      const [editorOptions, current] = await Promise.all([
        apiFetch("Article/GetEditorOptions"),
        editing
          ? apiFetch(
              `Article/GetArticleForEditor?slug=${encodeURIComponent(slug)}`,
            )
          : Promise.resolve(null),
      ]);
      if (cancelled) return;
      if (
        !editorOptions.ok ||
        (editing && (!current?.ok || !current.data?.articleId))
      ) {
        setLoadError(
          editorOptions.error || current?.error || "مقاله پیدا نشد.",
        );
        setLoading(false);
        return;
      }
      setOptions(editorOptions.data);
      if (current) {
        setArticle({ ...emptyArticle, ...current.data });
        setCategories(
          (current.data.articleCategories || []).map((item) => ({
            value: item.categoryId,
            label: item.category?.categoryName || String(item.categoryId),
          })),
        );
        setTags(
          (current.data.articleTagTbls || []).map((item) => ({
            value: item.tagId,
            label: item.tag?.tag || String(item.tagId),
          })),
        );
      } else {
        setArticle(emptyArticle);
        setCategories([]);
        setTags([]);
      }
      allowNavigation.current = false;
      setDirty(false);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [editing, slug, reload]);
  useEffect(() => {
    if (editing && slug && !new URLSearchParams(location.search).has("slug")) {
      navigate(
        {
          pathname: location.pathname,
          search: `?slug=${encodeURIComponent(slug)}`,
        },
        { replace: true, state: location.state },
      );
    }
  }, [
    editing,
    slug,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);
  useEffect(() => {
    const warn = (event) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const update = (name, value) => {
    setArticle((current) => ({ ...current, [name]: value }));
    setDirty(true);
    setSuccess("");
  };
  const save = async () => {
    setAttempted(true);
    setError("");
    setSuccess("");
    if (Object.values(validation).some(Boolean)) {
      setError("موارد مشخص‌شده را کامل کنید؛ اطلاعات شما حفظ شده است.");
      setTab("write");
      if (validation.articleTittleEn || validation.slugEn || validation.newsTextEn || validation.metaTitleEn || validation.metaDescriptionEn) setContentLanguage("en");
      return;
    }
    setSaving(true);
    const publicationDate =
      !isAdmin || publication === "keep"
        ? article.datePublished || null
        : publication === "schedule"
          ? schedule
          : new Date(Date.now() + 210 * 60000).toISOString().slice(0, 19);
    const result = await apiFetch(
      `Article/${editing ? "UpdateArticle" : "CreateArticle"}`,
      "post",
      articlePayload(article, categories, tags, publicationDate),
    );
    if (
      !result.ok ||
      (typeof result.data === "string" && result.data && result.data !== "true")
    ) {
      setSaving(false);
      setError(result.error || result.data || "ذخیره مقاله انجام نشد.");
      return;
    }
    setDirty(false);
    setSuccess(
      isAdmin
        ? "مقاله با موفقیت ذخیره شد."
        : "مقاله ذخیره شد؛ وضعیت تأیید را در گزارش مقالات ببینید.",
    );
    const savedSlug = article.slug
      .trim()
      .toLowerCase()
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    if (!editing || savedSlug !== slug) {
      allowNavigation.current = true;
      navigate(
        `/mainAdmin/editNews/release?slug=${encodeURIComponent(savedSlug)}`,
        { replace: true },
      );
    } else {
      const refreshed = await apiFetch(
        `Article/GetArticleForEditor?slug=${encodeURIComponent(savedSlug)}`,
      );
      if (refreshed.ok && refreshed.data?.articleId)
        setArticle({ ...emptyArticle, ...refreshed.data });
    }
    setSaving(false);
  };
  const uploadCover = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (
      !/^image\/(jpeg|png|webp|gif)$/.test(file.type) ||
      file.size > 1000000
    ) {
      setError(
        "تصویر JPG، PNG، WebP یا GIF با حجم حداکثر یک مگابایت انتخاب کنید.",
      );
      return;
    }
    setUploading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("FileDetails", file);
      const result = await api.post("Article/UploadPhoto", form);
      if (typeof result.data !== "string" || !result.data.trim())
        throw new Error();
      update("mainPicture", result.data);
    } catch {
      setError("بارگذاری تصویر انجام نشد. تصویر قبلی حفظ شده است.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };
  const createTag = async (label) => {
    label = label.trim();
    if (!label || tagBusy) return;
    const existing = options.tags.find(
      (item) => item.label.toLocaleLowerCase() === label.toLocaleLowerCase(),
    );
    if (existing) {
      setTags((current) =>
        current.some((item) => item.value === existing.value)
          ? current
          : [...current, existing],
      );
      setDirty(true);
      return;
    }
    setTagBusy(true);
    const result = await apiFetch("Article/CreateTag", "post", { Tag: label });
    if (result.ok) {
      const refreshed = await apiFetch("Article/GetEditorOptions");
      const created = refreshed.data?.tags?.find(
        (item) => item.label.toLocaleLowerCase() === label.toLocaleLowerCase(),
      );
      if (created) {
        setOptions(refreshed.data);
        setTags((current) => [...current, created]);
        setDirty(true);
      } else
        setError(
          "برچسب ایجاد شد ولی فهرست به‌روز نشد. صفحه را پس از ذخیره مقاله دوباره باز کنید.",
        );
    } else setError(result.error);
    setTagBusy(false);
  };
  const field = (name, label, multiline = false, hint = "") => (
    <label
      className={`article-field ${attempted && validation[name] ? "is-invalid" : ""}`}
    >
      <span>{label}</span>
      {multiline ? (
        <textarea
          maxLength={name.startsWith("meta") ? (name.includes("Description") ? 500 : 200) : undefined}
          value={article[name] || ""}
          onChange={(event) => update(name, event.target.value)}
          rows={name.toLowerCase().includes("newsbox") ? 4 : 2}
        />
      ) : (
        <input
          maxLength={name.startsWith("meta") ? (name.includes("Description") ? 500 : 200) : undefined}
          value={article[name] || ""}
          onChange={(event) => update(name, event.target.value)}
          dir={name.toLowerCase().includes("slug") ? "auto" : undefined}
        />
      )}
      {hint && <small>{hint}</small>}
      {attempted && validation[name] && <em>{validation[name]}</em>}
    </label>
  );
  const preview = `<!doctype html><html lang="${contentLanguage}" dir="${contentLanguage === "en" ? "ltr" : "rtl"}"><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: http: data:; media-src https: http:; style-src 'unsafe-inline';"><style>body{font-family:Tahoma,sans-serif;color:#21364b;padding:32px;max-width:760px;margin:auto;line-height:2.2;overflow-wrap:anywhere}h1{font-size:28px;line-height:1.7}h2{font-size:22px}h3{font-size:18px}img,video{max-width:100%;height:auto;border-radius:12px}blockquote{border-inline-start:3px solid #377be5;padding:12px;background:#f3f7fc}a{color:#2869cc}table{border-collapse:collapse;max-width:100%}td,th{border:1px solid #ddd;padding:8px}</style></head><body><h1>${escapeHtml(localizedArticle.articleTittle || (contentLanguage === "en" ? "Article title" : "عنوان مقاله"))}</h1><p>${escapeHtml(localizedArticle.secondTitle)}</p>${article.mainPicture ? `<img src="${escapeHtml(assetUrl(article.mainPicture))}" alt="${escapeHtml(localizedArticle.articleTittle)}">` : ""}${localizedArticle.newsText || (contentLanguage === "en" ? "<p>The article preview will appear here.</p>" : "<p>پیش‌نمایش متن مقاله اینجا نمایش داده می‌شود.</p>")}</body></html>`;
  const isEnglishContent = contentLanguage === "en";
  const contentFields = isEnglishContent ? {
    title: "articleTittleEn", subtitle: "secondTitleEn", slug: "slugEn", summary: "newsBoxEn", body: "newsTextEn", metaTitle: "metaTitleEn", metaDescription: "metaDescriptionEn",
  } : { title: "articleTittle", subtitle: "secondTitle", slug: "slug", summary: "newsBox", body: "newsText", metaTitle: "metaTitle", metaDescription: "metaDescription" };
  if (loading || loadError)
    return (
      <div className="article-workspace">
        <div className="article-state" role={loadError ? "alert" : "status"}>
          <FileText size={32} />
          <h2>
            {loading ? "آماده‌سازی میز نوشتن…" : "دریافت اطلاعات انجام نشد"}
          </h2>
          {loadError && (
            <>
              <p>{loadError}</p>
              <button onClick={() => setReload((value) => value + 1)}>
                تلاش دوباره
              </button>
              <Link to="/mainAdmin/articles/report">گزارش مقالات</Link>
            </>
          )}
        </div>
      </div>
    );
  return (
    <div className="article-workspace" dir="rtl">
      <header className="article-header">
        <div>
          <span className="article-eyebrow">مجله اینفینیتی • میز تحریریه</span>
          <h1>{editing ? "ویرایش مقاله" : "یک مقاله تازه بنویسید"}</h1>
          <p>از ایده تا انتشار؛ محتوای خواندنی با ساختاری روشن.</p>
        </div>
        <Link className="article-report-link" to="/mainAdmin/articles/report">
          <ChartNoAxesCombined size={18} />
          گزارش مقالات
          <ArrowRight size={16} />
        </Link>
      </header>
      <div className="article-command">
        <div className="article-language-tabs" role="tablist" aria-label="زبان محتوای مقاله">
          <button type="button" role="tab" aria-selected={!isEnglishContent} onClick={() => setContentLanguage("fa")}>فارسی</button>
          <button type="button" role="tab" aria-selected={isEnglishContent} onClick={() => setContentLanguage("en")}>English</button>
        </div>
        <div className="article-tabs" role="tablist" aria-label="نمای مقاله">
          <button
            role="tab"
            aria-selected={tab === "write"}
            onClick={() => setTab("write")}
          >
            <FileText size={16} />
            نوشتن مقاله
          </button>
          <button
            role="tab"
            aria-selected={tab === "preview"}
            onClick={() => setTab("preview")}
          >
            <Eye size={16} />
            پیش‌نمایش
          </button>
        </div>
        <span className="article-save-state">
          {dirty
            ? "تغییرات ذخیره نشده"
            : editing
              ? "نسخه ذخیره‌شده"
              : "مقاله جدید"}
        </span>
        <button
          className="article-primary"
          disabled={saving || uploading || tagBusy || mediaPending}
          onClick={save}
        >
          <Save size={16} />
          {saving
            ? "در حال ذخیره…"
            : editing
              ? "ذخیره تغییرات"
              : !isAdmin
                ? "ارسال برای بررسی"
                : publication === "schedule"
                  ? "زمان‌بندی انتشار"
                  : "انتشار مقاله"}
        </button>
      </div>
      {error && (
        <div className="article-notice is-error" role="alert">
          <CircleAlert size={18} />
          {error}
        </div>
      )}
      {success && (
        <div className="article-notice is-success" role="status">
          <Check size={18} />
          {success}
        </div>
      )}
      {article.rejectDescription && article.articleStatusId === 3 && (
        <div className="article-notice is-error">
          علت رد مقاله: {article.rejectDescription}
        </div>
      )}
      <fieldset disabled={saving} className="article-layout">
        <main className="article-main-column" dir={isEnglishContent ? "ltr" : "rtl"}>
          {tab === "write" ? (
            <>
              <section className="article-panel article-title-panel">
                <div className="article-section-heading">
                  <span className="article-step">۰۱</span>
                  <div>
                    <h2>{isEnglishContent ? "Title and introduction" : "عنوان و معرفی"}</h2>
                    <p>{isEnglishContent ? "The reader's first impression of your article" : "اولین برخورد خواننده با مقاله شما"}</p>
                  </div>
                </div>
                {field(contentFields.title, isEnglishContent ? "Article title *" : "عنوان مقاله *", true)}
                {field(
                  contentFields.subtitle,
                  isEnglishContent ? "Subtitle" : "زیرعنوان",
                  false,
                  isEnglishContent ? "Optional; one sentence that supports the title." : "اختیاری؛ یک جمله برای کامل‌کردن عنوان.",
                )}
              </section>
              <section className="article-panel article-body-panel">
                <div className="article-section-heading">
                  <span className="article-step">۰۲</span>
                  <div>
                    <h2>{isEnglishContent ? "Article content *" : "متن مقاله *"}</h2>
                    <p>
                      {isEnglishContent ? "The main title comes from the field above; use H2 and H3 for sections." : "عنوان اصلی از بخش بالا می‌آید؛ برای بخش‌های متن از H2 و H3 استفاده کنید."}
                    </p>
                  </div>
                  <span className="article-word-count">
                    {analysis.words.toLocaleString(isEnglishContent ? "en-US" : "fa-IR")} {isEnglishContent ? "words" : "کلمه"} ·{" "}
                    {analysis.minutes.toLocaleString(isEnglishContent ? "en-US" : "fa-IR")} {isEnglishContent ? "min read" : "دقیقه مطالعه"}
                  </span>
                </div>
                <ArticleRichEditor
                  value={article[contentFields.body] || ""}
                  onChange={(value) => update(contentFields.body, value)}
                  disabled={saving}
                  onPendingChange={setMediaPending}
                  onPreview={() => setTab("preview")}
                />
                {attempted && validation[contentFields.body] && (
                  <p className="article-error">{validation[contentFields.body]}</p>
                )}
              </section>
              <section className="article-panel">
                <div className="article-section-heading">
                  <span className="article-step">۰۳</span>
                  <div>
                    <h2>{isEnglishContent ? "Search appearance" : "نمایش در جستجو"}</h2>
                    <p>
                      {isEnglishContent ? "Custom metadata is optional. Leave it blank to use the article title and summary." : "فیلدهای متا اختیاری‌اند؛ اگر خالی باشند، عنوان و خلاصه مقاله استفاده می‌شوند."}
                    </p>
                  </div>
                </div>
                {field(
                  contentFields.slug,
                  isEnglishContent ? "Page slug *" : "نامک صفحه *",
                  false,
                  editing
                    ? (isEnglishContent ? "Changing a published URL can break existing links." : "تغییر آدرس مقاله منتشرشده ممکن است لینک‌های قبلی را از دسترس خارج کند.")
                    : (isEnglishContent ? "Use a short, descriptive English slug." : "نامک کوتاه و مرتبط؛ فارسی یا انگلیسی."),
                )}
                <button
                  type="button"
                  className="article-text-button"
                  onClick={() =>
                    update(contentFields.slug, makeSlug(article[contentFields.title]))
                  }
                >
                  {isEnglishContent ? "Generate slug from title" : "ساخت نامک از عنوان"}
                </button>
                {field(
                  contentFields.summary,
                  isEnglishContent ? "Article summary and search description" : "خلاصه مقاله و توضیحات جستجو",
                  true,
                  `${(article[contentFields.summary] || "").length.toLocaleString(isEnglishContent ? "en-US" : "fa-IR")} ${isEnglishContent ? "characters · Write a specific summary; display length varies by device and search engine." : "نویسه · خلاصه‌ای دقیق و اختصاصی بنویسید؛ طول نمایش به دستگاه و موتور جستجو بستگی دارد."}`,
                )}
                {field(contentFields.metaTitle, isEnglishContent ? "Meta Title (optional)" : "عنوان متا (Meta Title)", false, `${(article[contentFields.metaTitle] || "").length} / 200`)}
                {field(contentFields.metaDescription, isEnglishContent ? "Meta Description (optional)" : "توضیحات متا (Meta Description)", true, `${(article[contentFields.metaDescription] || "").length} / 500`)}
                <div className="article-search-preview">
                  <span>
                    <Search size={14} />
                    پیش‌نمایش تقریبی نتیجه جستجو
                  </span>
                  <small dir="ltr">
                    {siteUrl(`/blog-details/${article[contentFields.slug] || "article-slug"}`)}
                  </small>
                  <h3>{article[contentFields.metaTitle]?.trim() || article[contentFields.title] || (isEnglishContent ? "Your article title" : "عنوان مقاله شما")}</h3>
                  <p>
                    {plainText(
                      article[contentFields.metaDescription]?.trim() || article[contentFields.summary] ||
                        article[contentFields.subtitle] ||
                        article[contentFields.body],
                    ).slice(0, 160) ||
                      (isEnglishContent ? "A summary that helps readers understand the article topic." : "خلاصه‌ای که به خواننده کمک می‌کند موضوع مقاله را بشناسد.")}
                  </p>
                </div>
              </section>
            </>
          ) : (
            <section className="article-panel">
              <div className="article-section-heading">
                <Eye size={20} />
                <div>
                  <h2>پیش‌نمایش مقاله</h2>
                  <p>نمای تقریبی محتوا پیش از ذخیره و انتشار</p>
                </div>
              </div>
              <iframe
                className="article-preview-frame"
                title="پیش‌نمایش محتوای مقاله"
                sandbox=""
                srcDoc={preview}
              />
            </section>
          )}
        </main>
        <aside className="article-aside">
          <section className="article-panel">
            <div className="article-section-heading">
              <h2>تنظیمات انتشار</h2>
              <span className="article-badge">
                {editing
                  ? article.articleStatusId === 1
                    ? "تأییدشده"
                    : article.articleStatusId === 3
                      ? "ردشده"
                      : "در انتظار تأیید"
                  : "جدید"}
              </span>
            </div>
            <label className="article-field">
              <span>دسته‌بندی *</span>
              <Select
                inputId="article-categories"
                aria-label="دسته‌بندی مقاله"
                isDisabled={saving}
                isRtl
                isMulti
                options={options.categories}
                value={categories}
                onChange={(value) => {
                  setCategories(value || []);
                  setDirty(true);
                }}
                placeholder="انتخاب دسته‌بندی"
                noOptionsMessage={() => "دسته‌بندی یافت نشد"}
              />
              {attempted && validation.categories && (
                <em>{validation.categories}</em>
              )}
            </label>
            <label className="article-field">
              <span>برچسب‌ها *</span>
              <CreatableSelect
                aria-label="برچسب‌های مقاله"
                isRtl
                isMulti
                isDisabled={tagBusy || saving}
                isLoading={tagBusy}
                options={options.tags}
                value={tags}
                onChange={(value) => {
                  setTags(value || []);
                  setDirty(true);
                }}
                onCreateOption={createTag}
                formatCreateLabel={(value) => `ایجاد «${value}»`}
                placeholder="انتخاب یا ساخت برچسب"
                noOptionsMessage={() => "برای ایجاد برچسب، نام آن را بنویسید"}
              />
              {attempted && validation.tags && <em>{validation.tags}</em>}
            </label>
            {isAdmin && (
              <>
                <label className="article-field">
                  <span>زمان انتشار</span>
                  <select
                    value={publication}
                    onChange={(event) => {
                      setPublication(event.target.value);
                      setDirty(true);
                    }}
                  >
                    {editing && (
                      <option value="keep">حفظ تاریخ انتشار فعلی</option>
                    )}
                    <option value="now">همین حالا</option>
                    <option value="schedule">انتشار در آینده</option>
                  </select>
                </label>
                {publication === "keep" && article.datePublished && (
                  <p className="article-hint">
                    تاریخ ثبت‌شده:{" "}
                    {new Date(article.datePublished).toLocaleString("fa-IR")}
                  </p>
                )}
                {publication === "schedule" && (
                  <label className="article-field">
                    <span>تاریخ و ساعت به وقت تهران</span>
                    <input
                      type="datetime-local"
                      dir="ltr"
                      value={schedule}
                      onChange={(event) => {
                        setSchedule(event.target.value);
                        setDirty(true);
                      }}
                    />
                    {attempted && validation.schedule && (
                      <em>{validation.schedule}</em>
                    )}
                  </label>
                )}
                <details className="article-details">
                  <summary>جایگاه مقاله در سایت</summary>
                  {[
                    ["mainPage", "بالای صفحه اصلی"],
                    ["isProminent", "مقاله شاخص دسته‌بندی"],
                    ["isLast", "مقاله مهم"],
                    ["isMostViewed", "بخش منتخب پربازدید"],
                  ].map(([name, label]) => (
                    <label className="article-checkbox" key={name}>
                      <input
                        type="checkbox"
                        checked={Boolean(article[name])}
                        onChange={(event) => update(name, event.target.checked)}
                      />
                      {label}
                    </label>
                  ))}
                </details>
              </>
            )}
          </section>
          <section className="article-panel">
            <div className="article-section-heading">
              <h2>تصویر شاخص *</h2>
              <ImagePlus size={18} />
            </div>
            <input
              ref={coverInput}
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={uploadCover}
            />
            <button
              className="article-cover"
              type="button"
              disabled={uploading}
              onClick={() => coverInput.current.click()}
            >
              {article.mainPicture ? (
                <img
                  src={assetUrl(article.mainPicture)}
                  alt={article.articleTittle || "تصویر شاخص"}
                />
              ) : (
                <>
                  <ImagePlus size={30} />
                  <span>انتخاب تصویر شاخص</span>
                </>
              )}
            </button>
            <div className="article-button-row">
              <button
                type="button"
                disabled={uploading}
                onClick={() => coverInput.current.click()}
              >
                {uploading
                  ? "در حال بارگذاری…"
                  : article.mainPicture
                    ? "تغییر تصویر"
                    : "بارگذاری تصویر"}
              </button>
              {article.mainPicture && (
                <button type="button" onClick={() => update("mainPicture", "")}>
                  حذف از مقاله
                </button>
              )}
            </div>
            <p className="article-hint">
              JPG، PNG، WebP یا GIF · حداکثر ۱ مگابایت
            </p>
            {attempted && validation.mainPicture && (
              <p className="article-error">{validation.mainPicture}</p>
            )}
          </section>
          <section className="article-panel article-quality">
            <div className="article-section-heading">
              <h2>بررسی محتوا و سئو</h2>
              <b>
                {analysis.complete.toLocaleString("fa-IR")} /{" "}
                {analysis.checks.length.toLocaleString("fa-IR")}
              </b>
            </div>
            <progress
              max={analysis.checks.length}
              value={analysis.complete}
              aria-label="کامل بودن چک‌لیست محتوا"
            />
            <p className="article-hint">
              چک‌لیست نگارش؛ این عدد رتبه گوگل یا امتیاز قطعی سئو نیست.
            </p>
            <ul>
              {analysis.checks.map((check) => (
                <li key={check.label} className={check.ok ? "is-complete" : ""}>
                  {check.ok ? <Check size={15} /> : <CircleAlert size={15} />}
                  <span>{check.label}</span>
                </li>
              ))}
            </ul>
          </section>
          {analysis.headings.length > 0 && (
            <section className="article-panel">
              <h2>ساختار مقاله</h2>
              <ol className="article-outline">
                {analysis.headings.map((heading, index) => (
                  <li
                    key={index}
                    style={{
                      paddingInlineStart: Math.max(0, heading.level - 2) * 10,
                    }}
                  >
                    <small>H{heading.level}</small>
                    {heading.text || "تیتر بدون متن"}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </aside>
      </fieldset>
      {blocker.state === "blocked" && (
        <div className="article-modal-backdrop">
          <div
            className="article-panel article-leave-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="article-leave-title"
          >
            <h2 id="article-leave-title">تغییرات ذخیره نشده‌اند</h2>
            <p>با خروج از این صفحه، تغییرات اخیر مقاله از دست می‌رود.</p>
            <div className="article-button-row">
              <button
                autoFocus
                type="button"
                className="article-primary"
                onClick={() => blocker.reset()}
              >
                ادامه ویرایش
              </button>
              <button type="button" onClick={() => blocker.proceed()}>
                خروج بدون ذخیره
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
